import role6Schema from '../../../../assets/forms/role-120200.form.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { evaluateConditional } from '../conditional/conditional-field.evaluator';
import { ConditionalRule } from '../conditional/conditional-field.model';

/**
 * Pure validator for Role 6 (Director Business Review).
 *
 * Mirrors the editor's role6-120200-director-business-review.component.ts
 * validation surface:
 *
 *  - Static required: fields where buildForm() attaches Validators.required —
 *    i.e. unconditionally required via EITHER `control.required === true` OR
 *    a `validators: [{ name: 'required' }]` entry without conditionalOn,
 *    AND no separate conditionalOn entry exists. Matches buildForm()'s
 *    expanded `isUnconditionallyRequired && !hasConditionalRequirement`
 *    branch (the pre-fix version only honoured the top-level flag and
 *    silently dropped fields declared via the validators array).
 *  - min validator: when `validators[name=='min'].min !== undefined`. Mirrors
 *    Angular's Validators.min — fails only when the numeric value is less
 *    than the threshold; empty/null values are treated as valid at this
 *    check (the required check handles emptiness).
 *  - pattern validator: when `validators[name=='pattern'].pattern` is set.
 *    Mirrors Angular's Validators.pattern — only fires on non-empty string
 *    values that fail the regex. Null/undefined pass through.
 *
 * As of 2026-07-17 role 6 has NO conditional field:
 * ssmt:DisclosureOfOtherBusinessReviewApplied is now always visible and never
 * required (the schema `conditional` block was removed). The generic
 * `conditional`-handling below is retained defensively — it mirrors the
 * component's schema-driven applyConditionalRequired() and is a no-op unless a
 * control re-introduces a `conditional` block.
 *
 * Parity target:
 *   this.businessReviewForm.valid in the editor after all conditional
 *   valueChanges have settled (reflected in updateSheetLevelStatus
 *   hasErrors: !isValid).
 */

interface SchemaValidator {
  name?: string;
  pattern?: string;
  min?: number;
  conditionalOn?: unknown;
}

interface SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: SchemaValidator[];
  ui?: { control?: string };
  conditional?: ConditionalRule;
}

const SCHEMA_CONTROLS: ReadonlyArray<SchemaControl> =
  role6Schema.controls as SchemaControl[];

/**
 * Controls that participate in validation. Excludes section headers exactly
 * as buildForm() does (type === 'SectionHeader' || ui.control === 'section-header').
 */
const VALIDATING_CONTROLS: ReadonlyArray<SchemaControl> = SCHEMA_CONTROLS.filter(
  (control) => control.type !== 'SectionHeader' && control.ui?.control !== 'section-header'
);

function hasConditionalRequirement(control: SchemaControl): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn !== undefined
    ) ?? false
  );
}

function hasUnconditionalRequiredViaValidators(control: SchemaControl): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn === undefined
    ) ?? false
  );
}

/**
 * Static required keys — fields where buildForm() adds Validators.required.
 * Precomputed for efficiency since the rule is pure schema-driven.
 *
 * A field is unconditionally required when EITHER `control.required === true`
 * OR the validators array contains a `{ name: 'required' }` entry without a
 * conditionalOn property. Mirrors the expanded buildForm() check that
 * replaced the original (top-level-flag-only) bug.
 */
const STATIC_REQUIRED_KEYS: ReadonlySet<string> = new Set(
  VALIDATING_CONTROLS.filter((control) => {
    const isUnconditional =
      control.required === true || hasUnconditionalRequiredViaValidators(control);
    return isUnconditional && !hasConditionalRequirement(control);
  }).map((control) => control.qname)
);

/**
 * (qname -> compiled RegExp) for fields with a pattern validator. Compiled
 * once at module load so per-validate calls are cheap.
 */
const PATTERN_BY_KEY: ReadonlyMap<string, RegExp> = (() => {
  const map = new Map<string, RegExp>();
  for (const control of VALIDATING_CONTROLS) {
    const patternValidator = control.validators?.find((validator) => validator.name === 'pattern');
    if (patternValidator?.pattern) {
      try {
        map.set(control.qname, new RegExp(patternValidator.pattern));
      } catch {
        // Invalid regex in schema — component would also fail to apply it,
        // so skipping here preserves parity.
      }
    }
  }
  return map;
})();

/**
 * (qname -> numeric min) for fields with a min validator.
 */
const MIN_BY_KEY: ReadonlyMap<string, number> = (() => {
  const map = new Map<string, number>();
  for (const control of VALIDATING_CONTROLS) {
    const minValidator = control.validators?.find((validator) => validator.name === 'min');
    if (minValidator && typeof minValidator.min === 'number') {
      map.set(control.qname, minValidator.min);
    }
  }
  return map;
})();

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Required-field errors. A control is required when:
 *  - it carries a `conditional` whose effect includes validation AND the rule
 *    is currently active (evaluated via the shared `evaluateConditional` — the
 *    same source the editor's applyConditionalRequired() and the projector
 *    use); role 6's only conditional is same-sheet (`ssmt:Others`), so no
 *    cross-sheet context is needed, or
 *  - it has no conditional and is in the static unconditional-required set.
 */
function checkRequiredErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of VALIDATING_CONTROLS) {
    const required = control.conditional
      ? control.conditional.appliesTo !== 'visibility' &&
        evaluateConditional(control.conditional, sheetData).validationActive
      : STATIC_REQUIRED_KEYS.has(control.qname);
    if (required && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

function checkMinErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [key, min] of MIN_BY_KEY) {
    const value = sheetData[key];
    // Angular's Validators.min treats null/undefined/'' as valid — only
    // complains when a parseable number is below the threshold.
    if (isEmpty(value)) continue;
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isNaN(numeric) && numeric < min) {
      errors[key] = `${key} must be >= ${min}`;
    }
  }
  return errors;
}

function checkPatternErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [key, pattern] of PATTERN_BY_KEY) {
    const value = sheetData[key];
    // Angular's Validators.pattern coerces the value to string; empty values
    // skip the check (the required validator handles emptiness).
    if (isEmpty(value)) continue;
    if (!pattern.test(String(value))) {
      errors[key] = `${key} has invalid format`;
    }
  }
  return errors;
}

export const role6DirectorBusinessReviewValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.directorBusinessReview,
  // Tier 1 by primary characteristic (required-field driven). Role 6 has no
  // cross-sheet dependency and (as of 2026-07-17) no conditional field.
  tier: 1,
  validate(
    sheetData: Record<string, any>,
    _context: SheetValidationContext
  ): PureSheetValidationResult {
    const errors: Record<string, string> = {
      ...checkRequiredErrors(sheetData),
      ...checkMinErrors(sheetData),
      ...checkPatternErrors(sheetData),
    };
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: false,
      errors,
    };
  },
};
